/**
 * Type guard functions for runtime type checking of Peacock assets
 */

import { PeacockNodeType } from "../../shared-atom-types";
import type {
  AssetResponse,
  SeriesResponse,
  MovieResponse,
  SeasonResponse,
  EpisodeResponse,
  SportsEventResponse,
  GroupResponse,
  CollectionResponse,
  LinkResponse,
  HubResponse,
  ClipResponse,
  LinearSlotResponse,
} from "./response-types";

/**
 * Type guard for Series response
 * @param asset - The asset to check
 * @returns True if the asset is a series
 */
function isSeries(asset: AssetResponse): asset is SeriesResponse {
  return asset.type === PeacockNodeType.Series;
}

/**
 * Type guard for Movie response
 * @param asset - The asset to check
 * @returns True if the asset is a movie
 */
function isMovie(asset: AssetResponse): asset is MovieResponse {
  return asset.type === PeacockNodeType.Programme;
}

/**
 * Type guard for Season response
 * @param asset - The asset to check
 * @returns True if the asset is a season
 */
function isSeason(asset: AssetResponse): asset is SeasonResponse {
  return asset.type === PeacockNodeType.Season;
}

/**
 * Type guard for Episode response
 * @param asset - The asset to check
 * @returns True if the asset is an episode
 */
function isEpisode(asset: AssetResponse): asset is EpisodeResponse {
  return asset.type === PeacockNodeType.Episode;
}

/**
 * Type guard for Sports event response
 * @param asset - The asset to check
 * @returns True if the asset is a sports event
 */
function isSportsEvent(asset: AssetResponse): asset is SportsEventResponse {
  return asset.type === PeacockNodeType.SLE;
}

/**
 * Type guard for Group response
 * @param node - The node to check
 * @returns True if the node is a group
 */
function isGroup(node: unknown): node is GroupResponse {
  return (node as GroupResponse)?.type === PeacockNodeType.Group;
}

/**
 * Type guard for Collection response
 * @param node - The node to check
 * @returns True if the node is a collection
 */
function isCollection(node: unknown): node is CollectionResponse {
  return (node as CollectionResponse)?.type === PeacockNodeType.Collection;
}

/**
 * Type guard for Link response
 * @param node - The node to check
 * @returns True if the node is a link
 */
function isLink(node: unknown): node is LinkResponse {
  return (node as LinkResponse)?.type === PeacockNodeType.Link;
}

/**
 * Type guard for Hub response
 * @param node - The node to check
 * @returns True if the node is a hub
 */
function isHub(node: unknown): node is HubResponse {
  return (node as HubResponse)?.type === PeacockNodeType.Hub;
}

/**
 * Type guard for Clip response
 * @param asset - The asset to check
 * @returns True if the asset is a clip
 */
function isClip(asset: AssetResponse): asset is ClipResponse {
  return asset.type === PeacockNodeType.Clip;
}

/**
 * Type guard for Linear Slot response
 * @param asset - The asset to check
 * @returns True if the asset is a linear slot
 */
function isLinearSlot(asset: AssetResponse): asset is LinearSlotResponse {
  return asset.type === PeacockNodeType.LinearSlot;
}

export {
  isSeries,
  isMovie,
  isSeason,
  isEpisode,
  isSportsEvent,
  isGroup,
  isCollection,
  isLink,
  isHub,
  isClip,
  isLinearSlot,
};
